import { escapeHtml } from '@/utils/escapeHtml';
import { isMultiUnit } from './sheetRows';

/**
 * The 100x150mm label stock, fed landscape, and everything the sheets printed on it share. The PACKING sheet
 * and the READY TO BE OUTBOUNDED pick list are the same paper with a different head: one table, one footer,
 * one set of rules for what is loud and what is a dash [SSD-7207, 10/7 label test].
 */
export const LABEL_PAGE = { widthMm: 150, heightMm: 100 } as const;

/** A cell the server left empty prints a hyphen in ink: a blank or a grey dash vanished on the thermal paper [10/7]. */
export const BLANK = '-';

/**
 * What the server prints in place of a value it does not have: dashes, and 미등록 for a product with no
 * registered barcode. The label prints all of them as the one hyphen the warehouse reads as "none".
 */
const BLANK_MARKERS: ReadonlySet<string> = new Set(['', '—', '–', '미등록']);

export const blankAsDash = (value: string | number | null | undefined): string => {
  const text = String(value ?? '').trim();
  return BLANK_MARKERS.has(text) ? BLANK : text;
};

// Sheet geometry in millimetres. The stylesheet and the row-height estimate read the same numbers, so a
// change to one moves the other: that is what keeps the estimate honest.
export const SHEET_PADDING = { top: 2.4, side: 2.8, bottom: 5.4 } as const;
const FONT_MM = 3.8;
const LINE_HEIGHT = 1.25;
const CELL_PADDING_Y_MM = 1;
const CELL_PADDING_X_MM = 1.8;
const BORDER_MM = 0.2;
export const COLUMN_MM = { no: 8.8, ean: 15.6, size: 16.8, loc: 22.4, qty: 12.8 } as const;
/** What the product name column has left once the fixed columns, the sheet's side padding and its own padding are out. */
export const NAME_WIDTH_MM =
  LABEL_PAGE.widthMm -
  SHEET_PADDING.side * 2 -
  (COLUMN_MM.no + COLUMN_MM.ean + COLUMN_MM.size + COLUMN_MM.loc + COLUMN_MM.qty) -
  CELL_PADDING_X_MM * 2 -
  BORDER_MM * 2;

/** A long name wraps onto two or three lines instead of ending in an ellipsis [10/7]; past three it is clipped. */
export const MAX_NAME_LINES = 3;

/** One-line rows a sheet holds beside the closing row and the footer: the capacity the 8-row label test fixed. */
export const ROWS_PER_SHEET = 8;

/**
 * How wide a character prints, in ems, on the sans face the sheet asks for. The 10/7 label test measured
 * about 19 Korean syllables to the name column in a headless render, so a syllable is taken as 0.9em
 * (a touch wide); Latin and digits are narrower. Each figure errs wide, so a name is only ever estimated
 * to take more lines than it will, never fewer: a sheet may end a row early, never run over.
 */
const charWidthEm = (ch: string): number => {
  const code = ch.codePointAt(0) ?? 0;
  if (ch === ' ') {
    return 0.3;
  }
  if (/[.,:;'`|!il]/.test(ch)) {
    return 0.32;
  }
  if (/[-()[\]/+*"]/.test(ch)) {
    return 0.45;
  }
  if (/[0-9]/.test(ch)) {
    return 0.62;
  }
  if (/[A-Z]/.test(ch)) {
    return 0.72;
  }
  if (/[a-z]/.test(ch)) {
    return 0.58;
  }
  if (
    (code >= 0x1100 && code <= 0x11ff) ||
    (code >= 0x2e80 && code <= 0x9fff) ||
    (code >= 0xac00 && code <= 0xd7af) ||
    (code >= 0xf900 && code <= 0xfaff) ||
    (code >= 0xff00 && code <= 0xffef)
  ) {
    return 0.9;
  }
  return 0.75;
};

const widthEm = (text: string): number => [...text].reduce((sum, ch) => sum + charWidthEm(ch), 0);

/**
 * How many lines a product name takes in its column. The sheet breaks between words (word-break: keep-all)
 * and only inside a word that is wider than the column (overflow-wrap: anywhere), and this walks the same
 * way. Capped at MAX_NAME_LINES because the sheet clips there.
 */
export const nameLinesOf = (name: string, widthMm = NAME_WIDTH_MM): number => {
  const limit = widthMm / FONT_MM;
  let lines = 1;
  let used = 0;
  for (const word of name.trim().split(/\s+/).filter(Boolean)) {
    const width = widthEm(word);
    const gap = used > 0 ? charWidthEm(' ') : 0;
    if (used + gap + width <= limit) {
      used += gap + width;
      continue;
    }
    if (width <= limit) {
      lines += 1;
      used = width;
      continue;
    }
    // Wider than the column: it starts a line of its own and then breaks wherever the column ends.
    if (used > 0) {
      lines += 1;
      used = 0;
    }
    for (const ch of word) {
      const charWidth = charWidthEm(ch);
      if (used > 0 && used + charWidth > limit) {
        lines += 1;
        used = 0;
      }
      used += charWidth;
    }
  }
  return Math.min(Math.max(lines, 1), MAX_NAME_LINES);
};

const rowHeightMm = (lines: number): number => lines * FONT_MM * LINE_HEIGHT + CELL_PADDING_Y_MM * 2 + BORDER_MM;

/** A row's height in one-line rows, so a sheet's capacity can be spent on rows of any height. */
export const rowUnits = (lines: number): number => rowHeightMm(lines) / rowHeightMm(1);

export interface SheetPage<R> {
  rows: R[];
  pageNo: number;
  pageCount: number;
  /** The running total lands only on the last sheet; earlier ones say the order carries on. */
  closing: 'TOTAL' | 'CONTINUES';
}

/**
 * Cuts rows into sheets by the height they take, in one-line rows, so a wrapped name costs the lines it
 * uses and no sheet runs past its footer. An empty list still yields one (empty) sheet.
 */
export const paginateByHeight = <R>(rows: R[], unitsOf: (row: R) => number, capacity: number): Array<SheetPage<R>> => {
  const chunks: R[][] = [];
  let current: R[] = [];
  let used = 0;
  for (const row of rows) {
    const units = unitsOf(row);
    if (current.length > 0 && used + units > capacity + 1e-6) {
      chunks.push(current);
      current = [];
      used = 0;
    }
    current.push(row);
    used += units;
  }
  if (current.length > 0 || chunks.length === 0) {
    chunks.push(current);
  }
  return chunks.map((chunk, index) => ({
    rows: chunk,
    pageNo: index + 1,
    pageCount: chunks.length,
    closing: index === chunks.length - 1 ? 'TOTAL' : 'CONTINUES',
  }));
};

/**
 * The capacity the server paginated with, read off its own pages: the fullest page of a multi-page order
 * is the number of one-line rows it fits. Never more than the label test's eight, which this sheet's
 * geometry is drawn for; a single page says nothing about capacity, so the eight stands.
 */
export const sheetCapacityOf = (pages: Array<{ rows: unknown[] }>): number => {
  if (pages.length < 2) {
    return ROWS_PER_SHEET;
  }
  return Math.min(ROWS_PER_SHEET, Math.max(1, ...pages.map((page) => page.rows.length)));
};

/** One printed row, whichever sheet it is on. */
export interface SheetRow {
  no: number;
  eanLast4: string;
  name: string;
  size: string;
  location: string;
  qty: number;
  isSampleSet?: boolean;
}

export interface SheetHead {
  title: string;
  /** The order barcode drawn as an svg, or nothing on a sheet that is not an order's. */
  barcodeSvg?: string;
  totalQty: number;
}

/** The sample set is a courtesy, not a unit to count: its cell prints a hyphen instead of a quantity [SSD-7420]. */
const qtyCellHtml = (row: SheetRow): string =>
  row.isSampleSet
    ? `<td class="qty">${BLANK}</td>`
    : `<td class="qty${isMultiUnit(row.qty) ? ' multi' : ''}">${escapeHtml(row.qty)}</td>`;

const rowHtml = (row: SheetRow): string => `<tr${row.isSampleSet ? ' class="sample"' : ''}>
        <td>${escapeHtml(row.no)}</td>
        <td class="ean4">${escapeHtml(row.eanLast4)}</td>
        <td class="nm"><span class="nmx">${escapeHtml(row.name)}</span></td>
        <td>${escapeHtml(row.size)}</td>
        <td class="loc">${escapeHtml(row.location)}</td>
        ${qtyCellHtml(row)}
      </tr>`;

const closingHtml = (page: SheetPage<SheetRow>, totalQty: number): string =>
  page.closing === 'TOTAL'
    ? `<tr class="total"><td colspan="5">합계</td><td class="qty">${escapeHtml(totalQty)}</td></tr>`
    : '<tr class="total continues"><td colspan="5">계속 →</td><td class="qty"></td></tr>';

/** One 150x100 sheet: head, the six-column table, the closing row and the footer line. */
export const sheetHtml = (head: SheetHead, page: SheetPage<SheetRow>, stamp: string): string => `<section class="sheet">
    <header class="hd">
      <span class="ttl">${escapeHtml(head.title)}</span>
      <span class="bc">${head.barcodeSvg ?? ''}</span>
      <span class="tq">총수량: ${escapeHtml(head.totalQty)}</span>
    </header>
    <table>
      <colgroup><col class="c-no"><col class="c-ean"><col><col class="c-size"><col class="c-loc"><col class="c-qty"></colgroup>
      <thead><tr><th>No</th><th>바코드 뒤4</th><th>상품명</th><th>사이즈</th><th>로케이션</th><th>수량</th></tr></thead>
      <tbody>${page.rows.map(rowHtml).join('')}${closingHtml(page, head.totalQty)}</tbody>
    </table>
    <footer class="ft"><span>[검수 후 폐기]</span><span>${escapeHtml(page.pageNo)}/${escapeHtml(page.pageCount)}</span><span>${escapeHtml(stamp)}</span></footer>
  </section>`;

/** The typeface and ink of a sheet; set on the document body, or on the scope when the sheets sit inside another print. */
const SHEET_TYPE =
  "font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif; color: #000; background: #fff;";

/**
 * The sheet's own rules as selector list and declarations, so the same rules can be re-scoped when the
 * sheets print behind another document (a drawn Deleo-family sticker carries the PACKING sheet in one job).
 */
const SHEET_RULES: ReadonlyArray<readonly [string, string]> = [
  // The extra bottom margin keeps the footer clear of the label printer's unprintable edge; 8 rows still fit.
  [
    '.sheet',
    `width: ${LABEL_PAGE.widthMm}mm; height: ${LABEL_PAGE.heightMm}mm; padding: ${SHEET_PADDING.top}mm ${SHEET_PADDING.side}mm ${SHEET_PADDING.bottom}mm; box-sizing: border-box; display: flex; flex-direction: column; overflow: hidden; break-after: page;`,
  ],
  ['.sheet:last-child', 'break-after: auto;'],
  ['.hd', 'display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 2.4mm; margin-bottom: 1.6mm;'],
  // The order number is the string the packer matches against the parcel, so it reads as large as the title.
  ['.ttl', 'font-weight: 800; font-size: 5.2mm; line-height: 1.1; white-space: nowrap;'],
  ['.bc svg', 'display: block;'],
  ['.tq', 'font-weight: 800; font-size: 4.4mm; text-align: right; white-space: nowrap;'],
  [
    'table',
    `width: 100%; border-collapse: collapse; table-layout: fixed; font-size: ${FONT_MM}mm; line-height: ${LINE_HEIGHT};`,
  ],
  ['.c-no', `width: ${COLUMN_MM.no}mm;`],
  ['.c-ean', `width: ${COLUMN_MM.ean}mm;`],
  ['.c-size', `width: ${COLUMN_MM.size}mm;`],
  ['.c-loc', `width: ${COLUMN_MM.loc}mm;`],
  ['.c-qty', `width: ${COLUMN_MM.qty}mm;`],
  [
    'th, td',
    `border: ${BORDER_MM}mm solid #000; padding: ${CELL_PADDING_Y_MM}mm ${CELL_PADDING_X_MM}mm; text-align: center;`,
  ],
  ['th', 'font-size: 3.2mm; font-weight: 700;'],
  // A long name wraps between words onto two or three lines and is clipped past the third [10/7]: the paper
  // truncates nothing it can show, and the stored name is never touched.
  ['td.nm', 'text-align: left; white-space: normal; word-break: keep-all; overflow-wrap: anywhere;'],
  [
    'td.nm .nmx',
    `display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: ${MAX_NAME_LINES}; overflow: hidden;`,
  ],
  [
    'td.ean4',
    "font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-weight: 700; text-align: center; font-variant-numeric: tabular-nums;",
  ],
  [
    'td.loc',
    "font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-weight: 700; font-size: 3.4mm; color: #000;",
  ],
  ['td.qty', 'font-weight: 800; font-size: 4.2mm;'],
  // A thermal head prints no tint, so emphasis is drawn in ink: multi-unit counts and the sample set underlined.
  ['td.qty.multi', 'text-decoration: underline; text-decoration-thickness: 0.3mm; text-underline-offset: 0.5mm;'],
  ['tr.sample td', 'font-weight: 800;'],
  ['tr.sample td.nm', 'text-decoration: underline; text-decoration-thickness: 0.3mm; text-underline-offset: 0.5mm;'],
  ['tr.total td', 'font-weight: 800;'],
  ['tr.continues td:first-child', 'text-align: right;'],
  // Centred on the label's own centre line whatever the side items measure; one line, or capacity changes.
  [
    '.ft',
    'display: grid; grid-template-columns: 1fr auto 1fr; font-size: 2.7mm; margin-top: auto; padding-top: 1.2mm;',
  ],
  ['.ft span:nth-child(2)', 'text-align: center;'],
  ['.ft span:last-child', 'text-align: right; white-space: nowrap;'],
];

/**
 * The sheet rules as CSS. Unscoped they style a document of sheets; given a scope (a selector for the
 * element the sheets sit in) every rule is confined to it, the scope carries the sheet's own type and
 * ink, and the admin page's styles around it never reach the paper.
 */
export const sheetCss = (scope = ''): string => {
  const prefix = scope === '' ? '' : `${scope} `;
  const rules = SHEET_RULES.map(
    ([selectors, body]) =>
      `${selectors
        .split(',')
        .map((selector) => prefix + selector.trim())
        .join(', ')} { ${body} }`,
  );
  if (scope !== '') {
    rules.unshift(`${scope} { ${SHEET_TYPE} }`);
  }
  return rules.join('\n      ');
};

/**
 * The sheets as a document of their own, so the label printer gets a page the size of the sticker and the
 * admin page's own styles never reach the paper. On screen (the previews) consecutive sheets are set apart
 * by a dashed rule; on paper each one is a sticker.
 */
export const sheetDocument = (title: string, sheets: string[]): string => {
  const { widthMm, heightMm } = LABEL_PAGE;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>
      @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; }
      body { ${SHEET_TYPE} }
      /* On screen the document sits in a frame cut to its sheets, so it never grows its own scrollbars: a
         classic scrollbar would eat the frame's width and push the sticker's right edge out of view. */
      @media screen { html, body { overflow: hidden; } .sheet + .sheet { border-top: 0.5mm dashed #999; } }
      ${sheetCss()}
    </style></head><body>
    ${sheets.join('')}
  </body></html>`;
};
